/**
 * Workspace state that lives in the shell (plan §5b): the person's Workspace
 * preferences, one socket per tab to /crm/workspace/ws (ADR 0022) with its
 * events, chat states and the Fan replied badge, and who the person is in the
 * CRM. Mounted for everyone; it only connects when the person can open at
 * least one OnlyFans Account in the CRM.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { setUnreadChats } from '../components/unreadChats';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { CRM_FRAME_PATH } from '../crm/crmBridge';
import { useCrmHost } from '../crm/CrmHostContext';
import type { CrmAccount } from '../crm/crmAccess';
import type { WorkspaceEvent } from './activity';
import { fetchCrmMe, fetchPreferences, savePreferences } from './api';
import { windowHours, type ActiveWindow, type ChatStateRow } from './chats';
import { EMPTY_LIVE, FrameBatch, LIVE_TICK_MS, listAccountsOf, type LiveSnapshot } from './liveStore';
import { createDebouncedSaver } from './saver';
import { forgetFeeds } from './useFeed';

export type LiveStatus = 'off' | 'connecting' | 'live' | 'reconnecting';

/** settings jsonb (crm_workspace_preferences.settings). Widgets add their own keys. */
export interface WorkspaceSettings {
  /** Account ids, or 'all'. */
  scope?: string[] | 'all';
  activeWindow?: ActiveWindow;
  sound?: boolean;
  [key: string]: unknown;
}

/**
 * The slow-changing part. Live data (events, chat states, the badge) sits in
 * its own contexts and changes at most once per tick (liveStore), so only
 * what reads it re-renders.
 */
export interface WorkspaceContextValue {
  enabled: boolean;
  /** OnlyFans Accounts this person can open in the CRM. */
  accounts: CrmAccount[];
  me: { id: string; username: string } | null;
  status: LiveStatus;
  /** Account ids the server is streaming (scope ∩ access). */
  scopeIds: string[];
  prefsLoaded: boolean;
  layout: Record<string, unknown> | null;
  settings: WorkspaceSettings;
  /** Merge a patch (or a patch computed from the latest settings) and save it. */
  updateSettings: (patch: Partial<WorkspaceSettings> | ((prev: WorkspaceSettings) => Partial<WorkspaceSettings>)) => void;
  saveLayout: (layout: Record<string, unknown>) => void;
  /** Called for each live event (not replays), every kind, once per tick; returns an unsubscribe. */
  onEvent: (listener: (ev: WorkspaceEvent) => void) => () => void;
  /** Called with the chat state rows that changed, once per tick; returns an unsubscribe. */
  onChatStates: (listener: (rows: ChatStateRow[]) => void) => () => void;
}

const WorkspaceContext = createContext<WorkspaceContextValue | undefined>(undefined);
const LiveContext = createContext<LiveSnapshot>(EMPTY_LIVE);
const BadgeContext = createContext(0);
// OnlyFans and Fansly Accounts (ADR 0045).
const inWorkspace = (a: CrmAccount) => ['onlyfans', 'of', 'fansly'].includes(a.platform.trim().toLowerCase());

function socketUrl(): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}${CRM_FRAME_PATH}workspace/ws`;
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const crm = useCrmHost();
  const accounts = useMemo(() => crm.crmAccounts.filter(inWorkspace), [crm.crmAccounts]);
  const enabled = isAuthenticated && crm.access.state === 'ok' && accounts.length > 0;

  const [me, setMe] = useState<{ id: string; username: string } | null>(null);
  const [status, setStatus] = useState<LiveStatus>('off');
  const [live, setLive] = useState<LiveSnapshot>(EMPTY_LIVE);
  const [badge, setBadge] = useState(0);
  const [scopeIds, setScopeIds] = useState<string[]>([]);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [layout, setLayout] = useState<Record<string, unknown> | null>(null);
  const [settings, setSettings] = useState<WorkspaceSettings>({});
  const listeners = useRef(new Set<(ev: WorkspaceEvent) => void>());
  const stateListeners = useRef(new Set<(rows: ChatStateRow[]) => void>());
  const socketRef = useRef<WebSocket | null>(null);

  // Who am I in the CRM, and my saved Workspace preferences.
  useEffect(() => {
    if (!enabled) {
      forgetFeeds();
      setMe(null);
      setPrefsLoaded(false);
      setLayout(null);
      setSettings({});
      return;
    }
    let alive = true;
    fetchCrmMe().then((m) => alive && setMe(m));
    fetchPreferences()
      .then((p) => {
        if (!alive) return;
        setLayout(p.layout);
        setSettings((p.settings as WorkspaceSettings) || {});
      })
      .catch(() => undefined)
      .finally(() => alive && setPrefsLoaded(true));
    return () => {
      alive = false;
    };
  }, [enabled, user?.id]);
  // Remembered Suggestions belong to the person signed in.
  useEffect(() => forgetFeeds, [user?.id]);

  const settingsSaver = useMemo(() => createDebouncedSaver<WorkspaceSettings>((s) => savePreferences({ settings: s }), 1000), []);
  const layoutSaver = useMemo(() => createDebouncedSaver<Record<string, unknown>>((l) => savePreferences({ layout: l }), 1000), []);
  useEffect(() => {
    const flush = () => {
      settingsSaver.flush();
      layoutSaver.flush();
    };
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, [settingsSaver, layoutSaver]);

  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const updateSettings = useCallback(
    (patch: Partial<WorkspaceSettings> | ((prev: WorkspaceSettings) => Partial<WorkspaceSettings>)) => {
      // Computed outside the state updater so the save is queued once (StrictMode runs updaters twice).
      const prev = settingsRef.current;
      const next = { ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) };
      settingsRef.current = next;
      settingsSaver.queue(next);
      setSettings(next);
    },
    [settingsSaver],
  );
  const saveLayout = useCallback(
    (next: Record<string, unknown>) => {
      setLayout(next);
      layoutSaver.queue(next);
    },
    [layoutSaver],
  );

  const scope = settings.scope ?? 'all';
  const activeWindow = settings.activeWindow ?? 'today';
  // List events are only kept for Accounts with watched lists (or AI-ignored
  // fans switched on); a change re-sends the scope so the server replays what
  // was left out.
  const listAccounts = useMemo(() => listAccountsOf(settings.activity), [settings.activity]);
  const watchedKey = `${[...listAccounts.watched].sort().join()}|${listAccounts.aiIgnored}`;
  const watchedRef = useRef(listAccounts);
  watchedRef.current = listAccounts;
  const scopeMessage = useCallback(() => {
    const allowed = new Set(accounts.map((a) => a.account_id));
    const ids = scope === 'all' ? 'all' : scope.filter((id) => allowed.has(id));
    return JSON.stringify({
      type: 'scope',
      accountIds: ids === 'all' || ids.length ? ids : 'all',
      activeWindowHours: windowHours(activeWindow, Date.now()),
    });
    // watchedKey: a new watched list asks for a replay.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, scope, activeWindow, watchedKey]);
  const scopeRef = useRef(scopeMessage);
  scopeRef.current = scopeMessage;

  // Frames are collected and applied together every LIVE_TICK_MS: one render
  // (and one round of listener calls) per tick instead of one per frame.
  const batch = useRef(new FrameBatch());
  const liveRef = useRef(live);
  liveRef.current = live;
  const tick = useRef<number | null>(null);
  const flush = useCallback(() => {
    tick.current = null;
    if (batch.current.empty) return;
    const out = batch.current.drain(liveRef.current, watchedRef.current);
    liveRef.current = out.snapshot;
    setLive(out.snapshot);
    if (out.badge != null) setBadge(out.badge);
    if (out.states.length) stateListeners.current.forEach((fn) => fn(out.states));
    for (const ev of out.live) listeners.current.forEach((fn) => fn(ev));
  }, []);
  const schedule = useCallback(() => {
    if (tick.current == null) tick.current = window.setTimeout(flush, LIVE_TICK_MS);
  }, [flush]);
  useEffect(
    () => () => {
      if (tick.current != null) window.clearTimeout(tick.current);
      tick.current = null;
    },
    [],
  );

  // The socket: auth → scope → replay/live; reconnect with backoff.
  useEffect(() => {
    if (!enabled || !prefsLoaded) {
      setStatus('off');
      return;
    }
    let closed = false;
    let attempt = 0;
    let retry: number | null = null;

    const connect = () => {
      setStatus(attempt ? 'reconnecting' : 'connecting');
      const ws = new WebSocket(socketUrl());
      socketRef.current = ws;
      ws.onopen = () => ws.send(JSON.stringify({ type: 'auth', token: api.getToken() }));
      ws.onmessage = (msg) => {
        let frame: { type?: string; [k: string]: unknown };
        try {
          frame = JSON.parse(String(msg.data));
        } catch {
          return;
        }
        switch (frame.type) {
          case 'connected':
            attempt = 0;
            ws.send(scopeRef.current());
            break;
          case 'scope':
            setScopeIds(((frame.account_ids as string[]) || []).map(String));
            setStatus('live');
            break;
          case 'replay':
            batch.current.replay((frame.events as WorkspaceEvent[]) || []);
            schedule();
            break;
          case 'event':
            batch.current.event(frame.event as WorkspaceEvent);
            schedule();
            break;
          case 'chat_state':
            batch.current.chatState(frame.row as ChatStateRow);
            schedule();
            break;
          case 'unread_chats':
            setUnreadChats(String(frame.account_id ?? ''), Number(frame.count));
            break;
          case 'badge':
            batch.current.setBadge(Number(frame.count) || 0);
            schedule();
            break;
          default:
            break;
        }
      };
      ws.onclose = (e) => {
        if (socketRef.current === ws) socketRef.current = null;
        if (closed) return;
        // 4001 = the server refused the token: refresh it before trying again.
        if (e.code === 4001) void api.refreshAccessToken();
        attempt++;
        setStatus('reconnecting');
        retry = window.setTimeout(connect, Math.min(30_000, 1000 * 2 ** Math.min(attempt - 1, 5)));
      };
    };
    connect();
    return () => {
      closed = true;
      if (retry) window.clearTimeout(retry);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [enabled, prefsLoaded, user?.id, schedule]);

  // Scope or window changed: tell the live socket (it replays for the new scope).
  useEffect(() => {
    const ws = socketRef.current;
    if (ws && ws.readyState === WebSocket.OPEN && status === 'live') ws.send(scopeMessage());
    // status is deliberately not a dependency: only real scope changes resend.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeMessage]);

  useEffect(() => {
    if (!enabled) {
      batch.current.clear();
      liveRef.current = EMPTY_LIVE;
      setLive(EMPTY_LIVE);
      setBadge(0);
      setScopeIds([]);
    }
  }, [enabled]);

  const onEvent = useCallback((fn: (ev: WorkspaceEvent) => void) => {
    listeners.current.add(fn);
    return () => {
      listeners.current.delete(fn);
    };
  }, []);

  const onChatStates = useCallback((fn: (rows: ChatStateRow[]) => void) => {
    stateListeners.current.add(fn);
    return () => {
      stateListeners.current.delete(fn);
    };
  }, []);

  const value = useMemo<WorkspaceContextValue>(
    () => ({ enabled, accounts, me, status, scopeIds, prefsLoaded, layout, settings, updateSettings, saveLayout, onEvent, onChatStates }),
    [enabled, accounts, me, status, scopeIds, prefsLoaded, layout, settings, updateSettings, saveLayout, onEvent, onChatStates],
  );
  return (
    <WorkspaceContext.Provider value={value}>
      <LiveContext.Provider value={live}>
        <BadgeContext.Provider value={badge}>{children}</BadgeContext.Provider>
      </LiveContext.Provider>
    </WorkspaceContext.Provider>
  );
}

/** A Workspace without the socket or the server (tests of pages that read it). */
export function WorkspaceContextProvider({ value, live = EMPTY_LIVE, children }: { value: WorkspaceContextValue; live?: LiveSnapshot; children: ReactNode }) {
  return (
    <WorkspaceContext.Provider value={value}>
      <LiveContext.Provider value={live}>{children}</LiveContext.Provider>
    </WorkspaceContext.Provider>
  );
}

/** Events and chat states; changes at most once per tick. */
export function useWorkspaceLive(): LiveSnapshot {
  return useContext(LiveContext);
}

/** The Fan replied badge alone (the nav reads only this). */
export function useWorkspaceBadge(): number {
  return useContext(BadgeContext);
}

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace must be used within a WorkspaceProvider');
  return ctx;
}
